/**
 * DocViewSchema for Item DocType
 * 
 * This is the canonical example for DocViewSchema.
 * Copy this file and modify when adding new doctypes.
 * 
 * Tab structure:
 * - Details: Main product info (gallery, overview, general information, inventory)
 * - Inventory: Stock management sections
 * - Accounting: Account settings
 * - Purchase: Purchase settings
 * - Sales: Sales settings
 * - Tax: Tax templates
 * - Quality: Quality inspection settings
 * - Manufacturing: BOM and production settings
 * - Variants: Variant management (conditional: has_variants=1 or variant_of set)
 * - More Info: Unmapped meta fields
 * 
 * Widget naming convention:
 * - widgetId: "Item{SectionName}Widget" (e.g., "ItemInventoryWidget")
 * - Must match key in EXPERIENCE_WIDGETS registry
 */

import type { DocViewSchema } from "./types";

export const itemDocViewSchema: DocViewSchema = {
  doctype: "Item",
  views: {
    experience: {
      style: "shopify",

      // All sections available in this view
      sections: [
        // ===== Details Tab Sections =====
        // Order matches sectionIds for readability (sectionIds controls render order)
        {
          id: "disabled-banner",
          label: "Disabled Banner",
          description: "Warning banner for disabled items with Enable action",
          widgetId: "ItemDisabledBannerWidget",
        },
        {
          id: "item-overview",
          label: "Product Images & Overview",
          description: "Gallery, identification, and key metrics",
          widgetId: "ItemOverviewWidget",
        },
        {
          id: "product-details",
          label: "Product Details",
          description: "Core product identity: name, SKU, category, unit, brand, description",
          widgetId: "ItemProductDetailsWidget",
        },
        {
          id: "pricing",
          label: "Pricing",
          description: "Price lists, promotions, and pricing rules",
          widgetId: "ItemPricingWidget",
        },
        {
          id: "bundle-toggle",
          label: "Bundle Product",
          description: "Toggle to make this item a bundle of other items",
          widgetId: "ItemBundleToggleWidget",
        },
        {
          id: "bundle-components",
          label: "Bundle Components",
          description: "Component items in this product bundle",
          widgetId: "ItemBundleComponentsWidget",
        },
        {
          id: "variants-summary",
          label: "Variants",
          description: "Summary of variant products with attribute overview",
          widgetId: "ItemVariantsSummaryWidget",
        },
        {
          id: "booking",
          label: "Booking",
          description: "Enable booking/reservation with date and time slot picker",
          widgetId: "ItemBookingWidget",
        },
        {
          id: "storefront",
          label: "Storefront",
          description: "Storefront visibility and product URL settings",
          widgetId: "ItemStorefrontWidget",
        },
        {
          id: "marketplace-listings",
          label: "Marketplace Listings",
          description: "Connected marketplace listings for this product",
          widgetId: "ItemMarketplaceListingsWidget",
        },
        {
          id: "documents",
          label: "Product Documents",
          description: "PDF datasheets, manuals, certificates, and other downloadable files",
          widgetId: "ItemDocumentsWidget",
        },
        {
          id: "personalization",
          label: "Personalization",
          description: "Customization template and field configuration",
          widgetId: "ItemPersonalizationWidget",
        },
        {
          id: "collections",
          label: "Collections",
          description: "Manage which collections this item belongs to",
          widgetId: "ItemCollectionsWidget",
        },
        {
          id: "inventory",
          label: "Inventory Settings",
          description: "Stock, batch, serial number, and purchase/sales flags",
          widgetId: "ItemInventoryWidget",
        },
        {
          id: "advanced",
          label: "Advanced",
          description: "Rarely-used fields: foreign trade, disabled, valuation",
          widgetId: "ItemAdvancedWidget",
        },
        {
          id: "general",
          label: "General",
          description: "Additional item details and status",
          layout: "two-column",
          fields: [
            "item_type",
            "manufacturer",
            "manufacturer_part_no",
          ],
        },

        // ===== Inventory Tab Sections =====
        {
          id: "inventory-tab",
          label: "Inventory Settings",
          description: "Advanced inventory configuration",
          layout: "two-column",
          // Pull all fields from System View "inventory" tab dynamically
          // This ensures we always have the same fields as ERPNext inventory tab
          // @ts-expect-error - _pullFromSystemTab is a custom extension
          _pullFromSystemTab: "inventory",
        },

        // ===== Accounting Tab Sections =====
        {
          id: "accounting-tab",
          label: "Accounting Settings",
          description: "Account mappings and defaults",
          layout: "one-column",
          fields: ["item_defaults"],
        },

        // ===== Purchase Tab Sections =====
        {
          id: "purchase-tab",
          label: "Purchase Settings",
          description: "Purchasing configuration",
          layout: "two-column",
          fields: [
            "purchase_uom",
            "min_order_qty",
            "default_supplier",
            "delivered_by_supplier",
            "supplier_items",
          ],
        },

        // ===== Sales Tab Sections =====
        {
          id: "sales-tab",
          label: "Sales Settings",
          description: "Sales configuration",
          layout: "two-column",
          fields: [
            "sales_uom",
            "max_discount",
            "customer_items",
          ],
        },

        // ===== Tax Tab Sections =====
        {
          id: "tax-tab",
          label: "Tax Settings",
          description: "Tax templates and configuration",
          layout: "one-column",
          fields: ["taxes"],
        },

        // ===== Quality Tab Sections =====
        {
          id: "quality-tab",
          label: "Quality Settings",
          description: "Quality inspection configuration",
          layout: "two-column",
          fields: [
            "inspection_required_before_purchase",
            "inspection_required_before_delivery",
            "quality_inspection_template",
          ],
        },

        // ===== Manufacturing Tab Sections =====
        {
          id: "manufacturing-tab",
          label: "Manufacturing Settings",
          description: "BOM and production configuration",
          layout: "two-column",
          fields: [
            "default_bom",
            "include_item_in_manufacturing",
          ],
        },

        // ===== Variants Tab Sections =====
        {
          id: "variants-tab",
          label: "Variants",
          description: "Variant configuration and attributes",
          layout: "one-column",
          fields: ["variant_of", "attributes", "variant_based_on"],
        },

        // ===== Variations Tab Sections =====
        {
          id: "variations-tab",
          label: "Variations",
          description: "List of all product variations",
          widgetId: "ItemVariationsTabWidget",
        },

        // ===== More Info (unmapped fields) =====
        // Disabled: Users can switch to System View to see all fields
        // {
        //   id: "more-info",
        //   label: "Additional Information",
        //   description: "Additional ERP fields",
        //   layout: "one-column",
        //   // Empty fields = renderer should collect unmapped meta fields
        // },
      ],

      // Tab definitions - organize sections into navigation
      tabs: [
        {
          id: "details",
          label: "Details",
          sectionIds: ["disabled-banner", "product-details", "pricing", "bundle-toggle", "bundle-components", "variants-summary", "item-overview", "marketplace-listings", "booking", "storefront", "documents", "personalization", "inventory", "advanced", "general"],
        },
        {
          id: "inventory",
          label: "Inventory",
          sectionIds: ["inventory-tab"],
        },
        {
          id: "accounting",
          label: "Accounting",
          sectionIds: ["accounting-tab"],
        },
        {
          id: "purchase",
          label: "Purchase",
          sectionIds: ["purchase-tab"],
        },
        {
          id: "sales",
          label: "Sales",
          sectionIds: ["sales-tab"],
        },
        {
          id: "tax",
          label: "Tax",
          sectionIds: ["tax-tab"],
        },
        {
          id: "quality",
          label: "Quality",
          sectionIds: ["quality-tab"],
        },
        {
          id: "manufacturing",
          label: "Manufacturing",
          sectionIds: ["manufacturing-tab"],
        },
        {
          id: "variants",
          label: "Variants",
          sectionIds: ["variants-tab"],
          conditionalVisibility: {
            field: "has_variants_or_is_variant",
            value: true,
          },
        },
        {
          id: "variations",
          label: "Variations",
          sectionIds: ["variations-tab"],
          conditionalVisibility: {
            field: "has_variants",
            value: 1,
          },
        },
        // More Info tab disabled - users can switch to System View to see all fields
        // {
        //   id: "more-info",
        //   label: "More Info",
        //   sectionIds: ["more-info"],
        // },
      ],

      // Quick actions available in header/toolbar
      quickActions: [
        "quick_adjust_stock",
        "quick_create_item_price",
        "quick_create_stock_entry",
        "quick_duplicate_item",
      ],

      // Connection groups for Connections tab
      connectionGroups: ["pricing", "stock", "sales"],
    },

    // Create view: optimized for new item creation
    // Simplified layout with only essential fields, required fields first
    create: {
      style: "shopify",

      sections: [
        // Product Images section (first, for visual context)
        {
          id: "item-overview",
          label: "Product Images",
          description: "Upload and manage product images",
          widgetId: "ItemCreateOverviewWidget",
        },
        // Essential information for new items
        {
          id: "basic-info",
          label: "Basic Information",
          description: "Required fields to create a new item",
          layout: "two-column",
          fields: [
            "item_code", // Required
            "item_name", // Required
            "item_group",
            "stock_uom",
            "middleware_partner_key", // Supplier routing key for auto-pricing (Exogro) — set at manual creation to avoid missing keys
            "is_stock_item",
            "is_sales_item",
            "is_purchase_item",
          ],
        },
        // Template Toggle section (after basic info - SKU and name)
        {
          id: "template-toggle",
          label: "Variants",
          description: "Enable variant template mode",
          widgetId: "ItemTemplateToggleWidget",
        },
        // Bundle Toggle section (after template toggle — mutually exclusive with variants)
        {
          id: "bundle-toggle",
          label: "Bundle Product",
          description: "Toggle to make this item a bundle of other items",
          widgetId: "ItemBundleToggleWidget",
        },
        // Booking toggle (after bundle toggle)
        {
          id: "booking",
          label: "Booking",
          description: "Enable booking/reservation with date and time slot picker",
          widgetId: "ItemBookingWidget",
        },
        {
          id: "pricing-basic",
          label: "Pricing",
          description: "Basic pricing information",
          layout: "two-column",
          fields: [
            "standard_rate",
            "valuation_rate",
          ],
        },
        {
          id: "status",
          label: "Status",
          description: "Item status and availability",
          layout: "one-column",
          fields: [
            "disabled",
          ],
        },
      ],

      tabs: [
        {
          id: "details",
          label: "Details",
          sectionIds: ["item-overview", "basic-info", "template-toggle", "bundle-toggle", "booking", "pricing-basic", "status"],
        },
      ],

      // No quick actions in create mode (item doesn't exist yet)
      quickActions: [],

      // No connection groups in create mode
      connectionGroups: [],
    },

    // System view: raw/admin layout, field-focused
    // Uses _pullFromSystemTab to dynamically pull all fields from each ERPNext System View tab
    // This ensures System View matches ERPNext exactly without hardcoding field lists
    system: {
      sections: [
        // Pull all fields from ERPNext "details" tab (first/default tab)
        {
          id: "details",
          label: "Details",
          layout: "two-column",
          // @ts-expect-error - _pullFromSystemTab is a custom extension
          _pullFromSystemTab: "__details", // First tab (default, no Tab Break)
        },
        // Pull all fields from ERPNext "inventory" tab
        {
          id: "inventory",
          label: "Inventory",
          layout: "two-column",
          // @ts-expect-error - _pullFromSystemTab is a custom extension
          _pullFromSystemTab: "inventory",
        },
        // Pull all fields from ERPNext "accounting" tab
        {
          id: "accounting",
          label: "Accounting",
          layout: "two-column",
          // @ts-expect-error - _pullFromSystemTab is a custom extension
          _pullFromSystemTab: "accounting",
        },
        // Pull all fields from ERPNext "purchase" tab
        {
          id: "purchase",
          label: "Purchase",
          layout: "two-column",
          // @ts-expect-error - _pullFromSystemTab is a custom extension
          _pullFromSystemTab: "purchase",
        },
        // Pull all fields from ERPNext "sales" tab
        {
          id: "sales",
          label: "Sales",
          layout: "two-column",
          // @ts-expect-error - _pullFromSystemTab is a custom extension
          _pullFromSystemTab: "sales",
        },
        // Pull all fields from ERPNext "tax" tab
        {
          id: "tax",
          label: "Tax",
          layout: "two-column",
          // @ts-expect-error - _pullFromSystemTab is a custom extension
          _pullFromSystemTab: "tax",
        },
        // Pull all fields from ERPNext "quality" tab
        {
          id: "quality",
          label: "Quality",
          layout: "two-column",
          // @ts-expect-error - _pullFromSystemTab is a custom extension
          _pullFromSystemTab: "quality",
        },
        // Pull all fields from ERPNext "manufacturing" tab
        {
          id: "manufacturing",
          label: "Manufacturing",
          layout: "two-column",
          // @ts-expect-error - _pullFromSystemTab is a custom extension
          _pullFromSystemTab: "manufacturing",
        },
        // Pull all fields from ERPNext "variants" tab
        {
          id: "variants",
          label: "Variants",
          layout: "two-column",
          // @ts-expect-error - _pullFromSystemTab is a custom extension
          _pullFromSystemTab: "variants",
        },
      ],
      tabs: [
        {
          id: "details",
          label: "Details",
          sectionIds: ["details"],
        },
        {
          id: "inventory",
          label: "Inventory",
          sectionIds: ["inventory"],
        },
        {
          id: "accounting",
          label: "Accounting",
          sectionIds: ["accounting"],
        },
        {
          id: "purchase",
          label: "Purchase",
          sectionIds: ["purchase"],
        },
        {
          id: "sales",
          label: "Sales",
          sectionIds: ["sales"],
        },
        {
          id: "tax",
          label: "Tax",
          sectionIds: ["tax"],
        },
        {
          id: "quality",
          label: "Quality",
          sectionIds: ["quality"],
        },
        {
          id: "manufacturing",
          label: "Manufacturing",
          sectionIds: ["manufacturing"],
        },
        {
          id: "variants",
          label: "Variants",
          sectionIds: ["variants"],
        },
      ],
    },
  },
};
